// @ts-nocheck
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { streamSearchAvsOs } from "../utils/streamSearchAvsOs";
import { displaySourceLabel } from "../utils/sourceLabels";
import { showSourcePicker, isBetaSource, visibleSources, requestSourceTypes, sourcesStillLoading } from "../utils/sourcePicker";
import { CitationTwo } from "./CitationTwo";
import AnswerLink from "./AnswerLink";
import ClarifyPrompt from "./ClarifyPrompt";
import { planSubmit, requestForPick } from "../utils/clarify";
import * as services from "../Services/services";
import { usePageActions } from "../contexts/PageActionsContext";

const MAX_EXCHANGES = 6;

// ─── Citation pill ────────────────────────────────────────────────────────────

function CitationPill({ index, sources, onCitationClick }) {
  const source = sources?.[index - 1];
  if (!source) {
    return (
      <span className="c2-citation c2-citation--empty">{index}</span>
    );
  }
  return (
    <button
      type="button"
      className={`c2-citation${source.citationUrl ? "" : " c2-citation--empty"}`}
      onClick={() => source.citationUrl && onCitationClick?.(source)}
      title={source.title || `Source ${index}`}
    >
      {index}
    </button>
  );
}

function processRefs(children, sources, onCitationClick) {
  return React.Children.map(children, (child) => {
    if (typeof child !== "string") return child;
    const parts = child.split(/(\[\d+\])/g);
    if (parts.length === 1) return child;
    return parts.map((part, i) => {
      const m = part.match(/^\[(\d+)\]$/);
      if (m) return <CitationPill key={i} index={parseInt(m[1], 10)} sources={sources} onCitationClick={onCitationClick} />;
      return part;
    });
  });
}

// ─── Markdown with inline citation pills ─────────────────────────────────────

function MsgMarkdown({ content, sources, onCitationClick, onLinkClick }) {
  const components = useMemo(() => ({
    p:      ({ children, ...p }) => <p {...p}>{processRefs(children, sources, onCitationClick)}</p>,
    li:     ({ children, ...p }) => <li {...p}>{processRefs(children, sources, onCitationClick)}</li>,
    strong: ({ children, ...p }) => <strong className="fw-semibold" {...p}>{children}</strong>,
    ul:     ({ children, ...p }) => <ul className="c2-md-ul" {...p}>{children}</ul>,
    ol:     ({ children, ...p }) => <ol className="c2-md-ol" {...p}>{children}</ol>,
    code:   ({ children, className, ...p }) => {
      if (className?.startsWith("language-")) {
        return <pre className="c2-md-pre"><code {...p}>{children}</code></pre>;
      }
      return <code className="c2-md-code" {...p}>{children}</code>;
    },
    // ACOP-152 AC-12: a `[Title](#N)` link opens source N; see AnswerLink.
    a: ({ children, href, ...p }) => (
      <AnswerLink href={href} sources={sources} onCitationClick={onCitationClick} onLinkClick={onLinkClick} {...p}>
        {children}
      </AnswerLink>
    ),
  }), [sources, onCitationClick, onLinkClick]);

  return (
    <div className="c2-markdown">
      <ReactMarkdown components={components}>
        {DOMPurify.sanitize(content)}
      </ReactMarkdown>
    </div>
  );
}


// ─── Sources badge (below assistant bubble) ───────────────────────────────────

function SourcesBadge({ sources, onCitationClick }) {
  const [expanded, setExpanded] = useState(false);
  if (!sources?.length) return null;

  return (
    <div className="c2-sources">
      <button
        type="button"
        className="c2-sources-toggle"
        onClick={() => setExpanded((v) => !v)}
      >
        <svg
          className={`c2-sources-chevron${expanded ? " c2-sources-chevron--open" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {sources.length} source{sources.length !== 1 ? "s" : ""}
      </button>
      {expanded && (
        <div className="c2-sources-list">
          {sources.map((src, i) => {
            const inner = (
              <>
                <div className="c2-source-row">
                  <span className="c2-source-title">[{i + 1}] {src.title || "Untitled"}</span>
                  {src.similarity != null && (
                    <span className="c2-source-sim">{Math.round(src.similarity * 100)}%</span>
                  )}
                </div>
                <span className="c2-source-type">{formatSourceType(src.sourceType)}</span>
              </>
            );
            if (src.citationUrl) {
              return (
                <button
                  key={src.id || i}
                  type="button"
                  className="c2-source-item c2-source-item--link"
                  onClick={() => onCitationClick?.(src)}
                >
                  {inner}
                </button>
              );
            }
            return (
              <div key={src.id || i} className="c2-source-item">{inner}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Streaming / typing bubble ────────────────────────────────────────────────

function StreamingBubble({ text, sources, onCitationClick, onLinkClick }) {
  return (
    <div className="c2-msg c2-msg--assistant">
      <div className="c2-avatar c2-avatar--assistant">
        <i className="fa-solid fa-book-open-reader c2-avatar-icon" aria-hidden />
      </div>
      <div className="c2-bubble-wrap">
        <div className="c2-bubble c2-bubble--assistant">
          {text ? (
            <>
              <MsgMarkdown content={text} sources={sources} onCitationClick={onCitationClick} onLinkClick={onLinkClick} />
              <span className="c2-cursor" aria-hidden />
            </>
          ) : (
            <div className="c2-dots">
              <span style={{ animationDelay: "0ms" }} />
              <span style={{ animationDelay: "150ms" }} />
              <span style={{ animationDelay: "300ms" }} />
            </div>
          )}
        </div>
        {sources.length > 0 && <SourcesBadge sources={sources} onCitationClick={onCitationClick} />}
      </div>
    </div>
  );
}

// ─── Finalized message bubble ─────────────────────────────────────────────────

function MessageBubble({ message, userInitials: userInitialsValue, onCitationClick, onLinkClick }) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div className="c2-msg c2-msg--user">
        <div className="c2-bubble c2-bubble--user">
          {message.content}
        </div>
        <div className="c2-avatar c2-avatar--user">
          {userInitialsValue ? (
            <span className="c2-avatar-initials">{userInitialsValue}</span>
          ) : (
            <i className="fa-solid fa-user c2-avatar-icon" aria-hidden />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="c2-msg c2-msg--assistant">
      <div className="c2-avatar c2-avatar--assistant">
        <i className="fa-solid fa-book-open-reader c2-avatar-icon" aria-hidden />
      </div>
      <div className="c2-bubble-wrap">
        <div className="c2-bubble c2-bubble--assistant">
          {message.isError ? (
            <span className="text-danger">{message.content}</span>
          ) : (
            <MsgMarkdown content={message.content} sources={message.sources} onCitationClick={onCitationClick} onLinkClick={onLinkClick} />
          )}
        </div>
        {message.sources?.length > 0 && <SourcesBadge sources={message.sources} onCitationClick={onCitationClick} />}
      </div>
    </div>
  );
}

// ─── Topnav-anchored Customize popover ───────────────────────────────────────
//
// Lives inside the dashboard topnav slot so the page never grows a dynamic-height
// band of its own. The trigger is a compact button; the panel floats below it as
// an absolute popover that closes on outside click or Escape.

function CustomizePopover({ capabilities, selectedSources, onToggle }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const available = capabilities.filter((c) => c.available);
  const activeCount = selectedSources.length;
  const badgeCount = activeCount > 0 && activeCount < available.length ? activeCount : 0;

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="c2-customize-wrap" ref={wrapRef}>
      <button
        type="button"
        className="c2-topbar-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <i className="fa-solid fa-sliders" aria-hidden />
        <span>Sources</span>
        {badgeCount > 0 && (
          <span className="c2-customize-badge">{badgeCount}</span>
        )}
      </button>

      {open && (
        <div className="c2-customize-popover" role="dialog" aria-label="Customize sources">
          {capabilities.length > 0 ? (
            <div className="c2-customize-section">
              <div className="c2-customize-label">Sources</div>
              {capabilities.map((cap) => (
                <label
                  key={cap.key}
                  className={`c2-customize-row${!cap.available ? " c2-customize-row--disabled" : ""}`}
                  title={!cap.available ? "Coming soon" : cap.description || displaySourceLabel(cap)}
                >
                  <input
                    type="checkbox"
                    className="c2-customize-check"
                    checked={selectedSources.includes(cap.key)}
                    onChange={() => cap.available && onToggle(cap.key)}
                    disabled={!cap.available}
                  />
                  <span className="c2-customize-src-name">{displaySourceLabel(cap)}</span>
                  {isBetaSource(cap) && (
                    <i
                      className="fa-solid fa-flask c2-customize-beta-mark"
                      title="Beta source"
                      aria-label="beta"
                      data-testid={`beta-source-${cap.key}`}
                    />
                  )}
                  {cap.count != null && (
                    <span className="c2-customize-src-count">
                      {Number(cap.count).toLocaleString()}
                    </span>
                  )}
                </label>
              ))}
              {selectedSources.length > 0 && selectedSources.length < available.length && (
                <button
                  type="button"
                  className="c2-customize-clear"
                  onClick={() =>
                    available.forEach((c) => {
                      if (!selectedSources.includes(c.key)) onToggle(c.key);
                    })
                  }
                >
                  Clear source filters
                </button>
              )}
            </div>
          ) : (
            <div className="c2-customize-empty">No sources available.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Chat input ───────────────────────────────────────────────────────────────

function ChatInput({ onSubmit, isStreaming, followUpsRemaining, chatId }) {
  const [value, setValue] = useState("");
  const textareaRef = useRef(null);
  const atLimit = chatId !== null && followUpsRemaining <= 0;

  const handleSubmit = useCallback(() => {
    if (!value.trim() || isStreaming || atLimit) return;
    onSubmit(value.trim());
    setValue("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  }, [value, isStreaming, atLimit, onSubmit]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    }
  };

  return (
    <div className="c2-footer">
      {chatId && (
        <div className="c2-followup-bar">
          {atLimit
            ? "Follow-up limit reached — start a new chat"
            : `${followUpsRemaining} follow-up${followUpsRemaining !== 1 ? "s" : ""} remaining`}
        </div>
      )}
      <div className="c2-input-row">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={atLimit ? "Start a new chat..." : "Ask a question..."}
          disabled={isStreaming || atLimit}
          rows={1}
          className="c2-textarea"
        />
        <button
          type="button"
          className="c2-send-btn"
          onClick={handleSubmit}
          disabled={!value.trim() || isStreaming || atLimit}
          aria-label="Send"
        >
          {isStreaming ? (
            <span className="c2-send-spinner" />
          ) : (
            <svg className="c2-send-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function userInitials(name) {
  if (!name || typeof name !== "string") return "";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const CopilotTwo = ({ roles, userFullName, betaPreviewOn = false }) => {
  const navigate = useNavigate();
  const handleLinkClick = useCallback((href) => {
    if (href) navigate('/resources/embed?url=' + encodeURIComponent(href));
  }, [navigate]);
  const userInitialsValue = useMemo(() => userInitials(userFullName), [userFullName]);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  // ACOP-140: Beta Preview adds the employee-only EXTRA sources (internal KB, product knowledge,
  // tickets, change requests, feature requests); off, only the public git KB is offered and searched.
  // A non-employee never receives the extras at all (AVS OS), so for them this is the full list.
  const visibleCapabilities = useMemo(
    () => visibleSources(capabilities, betaPreviewOn === true),
    [capabilities, betaPreviewOn]
  );

  // Finalized messages (user + completed assistant)
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [messageCount, setMessageCount] = useState(0);

  // Live streaming state (mirrors AVS OS AiSearchProvider)
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [currentSources, setCurrentSources] = useState([]);
  const [error, setError] = useState(null);

  // Citation modal state
  const [citationSource, setCitationSource] = useState(null);
  const handleCitationClick = useCallback((source) => setCitationSource(source), []);
  const handleCitationClose = useCallback(() => setCitationSource(null), []);
  /*
   * Open the article the slide-out is showing at full size on Knowledge Base (ACOP-100).
   *
   * The navigating happens HERE rather than inside CitationTwo because that component deliberately
   * has no react-router-dom import — which is what makes it testable under react-scripts' Jest.
   *
   * Closing the panel is part of the action, not a side effect: leaving it open would park the
   * slide-out on top of the very article it just sent the reader to.
   */
  const handleOpenFullArticle = useCallback((urlname) => {
    setCitationSource(null);
    navigate("/knowledge-base-2?article=" + encodeURIComponent(urlname));
  }, [navigate]);

  // Clarify state
  const [clarifyState, setClarifyState] = useState(null);
  const pendingQuestionRef = useRef(null);

  const messagesEndRef = useRef(null);

  // ── Load capabilities ──────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const resp = await services.avsOsCapabilities({});
        const body = resp?.data;
        if (body?.status === 1 && body.data?.sources) {
          setCapabilities(body.data.sources);
        }
      } catch (err) {
        console.error("Failed to load AVS OS capabilities:", err);
      }
    })();
  }, []);

  // Reset the selection to everything VISIBLE whenever the capabilities load or the toggle flips, so
  // flipping Beta Preview on selects the extras and off drops them. When extras exist but are hidden,
  // the selection is a strict subset of `capabilities`, so the request below carries the explicit
  // base keys instead of letting an employee's default "all sources" pull the extras back in.
  useEffect(() => {
    setSelectedSources(visibleCapabilities.filter((s) => s.available).map((s) => s.key));
  }, [visibleCapabilities]);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  // ── Toggle source filter ───────────────────────────────────────────────────
  const toggleSource = useCallback((key) => {
    setSelectedSources((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  // ── New chat ───────────────────────────────────────────────────────────────
  const handleNewChat = useCallback(() => {
    setMessages([]);
    setChatId(null);
    setMessageCount(0);
    setIsStreaming(false);
    setStreamingText("");
    setCurrentSources([]);
    setError(null);
    setClarifyState(null);
    pendingQuestionRef.current = null;
  }, []);

  // ── Core submit ────────────────────────────────────────────────────────────
  const submitQuestion = useCallback(
    async (question, overrideSegment = null, overrideClarifyStep = null, overrideReply = null) => {
      if (!question || isStreaming) return;
      // An employee with Beta Preview off cannot be narrowed to the base KB until the capability list
      // has loaded, and sending without it would search every source (ACOP-140). Hold the send.
      if (sourcesStillLoading(capabilities, Array.isArray(roles) && roles.includes("employee"), betaPreviewOn === true)) {
        toast.info("Loading your knowledge sources \u2014 try again in a moment.");
        return;
      }

      const exchanges = Math.ceil(messageCount / 2);
      if (chatId && exchanges >= MAX_EXCHANGES) {
        toast.warning("Conversation limit reached. Starting a new chat.");
        handleNewChat();
        return;
      }

      setError(null);
      setIsStreaming(true);
      setStreamingText("");
      setCurrentSources([]);
      // Whatever was waiting for an answer has now been answered, or ignored for a new question.
      setClarifyState(null);
      pendingQuestionRef.current = null;

      // A pick (button or typed answer) resends the ORIGINAL question, already shown; only a fresh
      // question adds a bubble.
      if (!overrideSegment && !overrideReply) {
        setMessages((prev) => [...prev, { role: "user", content: question }]);
      }

      let accumulated = "";
      let capturedSources = [];

      try {
        await streamSearchAvsOs(
          {
            question,
            sourceTypes: requestSourceTypes(capabilities, selectedSources, betaPreviewOn === true),
            chatId: chatId || undefined,
            segment: overrideSegment || undefined,
            clarifyStep: overrideClarifyStep || undefined,
            // The answer to a clarifying question travels APART from the question it answers, so the
            // question is what gets searched (ACOP-147).
            clarifyReply: overrideReply || undefined,
            // The intent finder is for an employee with Beta Preview on. copilot-api forwards this
            // for an employee session only, and AVS OS treats absence as off: everyone else is
            // answered from the KB directly, nothing asked.
            intentFinder: betaPreviewOn === true ? true : undefined,
          },
          {
            onSources: (sources) => {
              capturedSources = sources;
              setCurrentSources(sources);
            },
            onToken: (text) => {
              accumulated += text;
              setStreamingText(accumulated);
            },
            onDone: (data) => {
              const assistantMsg = {
                role: "assistant",
                content: accumulated,
                sources: capturedSources.length > 0 ? capturedSources : [],
              };
              setMessages((prev) => [...prev, assistantMsg]);
              if (data.chatId) setChatId(data.chatId);
              if (data.messageCount) setMessageCount(data.messageCount);
              setStreamingText("");
              setIsStreaming(false);
            },
            onError: (err) => {
              setError(err.message || "Search error. Please try again.");
              setStreamingText("");
              setIsStreaming(false);
            },
            onClarify: (data) => {
              pendingQuestionRef.current = question;
              setClarifyState(data);
            },
          }
        );
      } catch {
        setError("An error occurred while searching. Please try again.");
        setStreamingText("");
        setIsStreaming(false);
      }
    },
    [isStreaming, messageCount, chatId, selectedSources, capabilities, betaPreviewOn, roles, handleNewChat]
  );

  // A pick — a button, or a typed "2" / "Copilot" resolved against the waiting question's options —
  // resends the ORIGINAL question with the answer alongside it (requestForPick). Searching the answer
  // as if it were the question was the stgn bug ("feature voting" -> "2" -> `"2 [Product: Copilot]"`).
  const handleClarifyPick = useCallback(async (option, typedText = null) => {
    const question = pendingQuestionRef.current;
    if (!question) return;
    const req = requestForPick(clarifyState?.clarifyStep, option);
    if (typedText) {
      setMessages((prev) => [...prev, { role: "user", content: typedText }]);
    }
    await submitQuestion(question, req.segment, req.clarifyStep, req.clarifyReply);
  }, [clarifyState, submitQuestion]);

  // What the input sends: an answer to the waiting question, or a fresh question. Text that is not one
  // of the offered options is ALWAYS a fresh question, so ignoring the prompt never traps anyone.
  const handleSubmit = useCallback(async (text) => {
    const plan = planSubmit(text, clarifyState, pendingQuestionRef.current);
    if (plan.kind === "pick") {
      await handleClarifyPick(plan.option, String(text).trim());
      return;
    }
    await submitQuestion(text);
  }, [clarifyState, handleClarifyPick, submitQuestion]);

  const followUpsRemaining = Math.max(0, MAX_EXCHANGES - Math.ceil(messageCount / 2));

  /*
   * Page actions live in the dashboard topnav. We register Customize + New
   * Chat with the PageActionsContext so DashboardTopNav renders them inline
   * with the page title — no DOM portal, no race conditions, and the topnav
   * stays a single fixed-height row regardless of viewport width.
   */
  const { setPageActions } = usePageActions();
  const newChatDisabled = isStreaming || messages.length === 0;
  useEffect(() => {
    setPageActions(
      <>
        {showSourcePicker(visibleCapabilities) && (
          <CustomizePopover
            capabilities={visibleCapabilities}
            selectedSources={selectedSources}
            onToggle={toggleSource}
          />
        )}
        <button
          type="button"
          className="c2-topbar-btn c2-topbar-btn--primary"
          onClick={handleNewChat}
          disabled={newChatDisabled}
          title="Start a new chat"
        >
          <i className="fa-solid fa-plus" aria-hidden />
          <span>New Chat</span>
        </button>
      </>,
    );
    return () => setPageActions(null);
  }, [
    setPageActions,
    visibleCapabilities,
    selectedSources,
    toggleSource,
    handleNewChat,
    newChatDisabled,
  ]);

  return (
    <div className="c2-container">
      {/* ── Messages ── */}
      <div className="c2-messages">
        {messages.length === 0 && !isStreaming && (
          <div className="c2-empty">
            <div className="c2-empty-icon-wrap">
              <i className="fa-solid fa-book-open-reader c2-empty-icon" aria-hidden />
            </div>
            <p className="c2-empty-title">Copilot</p>
            <p className="c2-empty-body">
              Search across the complete AvSight Knowledge Base for help with your questions.
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <React.Fragment key={i}>
            <MessageBubble message={msg} userInitials={userInitialsValue} onCitationClick={handleCitationClick} onLinkClick={handleLinkClick} />
            {i === messages.length - 1 && clarifyState && !isStreaming && (
              <ClarifyPrompt
                clarifyData={clarifyState}
                onPick={handleClarifyPick}
              />
            )}
          </React.Fragment>
        ))}

        {isStreaming && (
          <StreamingBubble text={streamingText} sources={currentSources} onCitationClick={handleCitationClick} onLinkClick={handleLinkClick} />
        )}

        {error && !isStreaming && (
          <div className="c2-error">{error}</div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Input ── */}
      <ChatInput
        onSubmit={handleSubmit}
        isStreaming={isStreaming}
        followUpsRemaining={followUpsRemaining}
        chatId={chatId}
      />

      {/* ── Citation modal ── */}
      {citationSource && (
        <CitationTwo
          initialSource={citationSource}
          onClose={handleCitationClose}
          onOpenFullArticle={handleOpenFullArticle}
        />
      )}
    </div>
  );
};

function formatSourceType(key) {
  const labels = {
    kb_article: "KB Article",
    support_case: "Support Case",
    change_request: "Change Request",
  };
  return labels[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Named only so the answer-link wiring can be tested (CopilotTwoMarkdown.test.tsx); the page uses the default.
export { MsgMarkdown };
export default CopilotTwo;
